import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import * as authApi from '../api/auth.js';
import { extractApiError } from '../api/client.js';
import { GoogleSignInButton } from '../components/GoogleSignInButton.js';
import { ANALYTICS_EVENTS, trackAuthEvent } from '../lib/analytics/index.js';

export function Register(): JSX.Element {
  const { t } = useTranslation(['auth', 'common']);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);

  if (status === 'sent') {
    return (
      <div className="card">
        <h2>{t('register.checkEmail.title')}</h2>
        <p>
          <Trans
            i18nKey="register.checkEmail.body"
            t={t}
            values={{ email }}
            components={[<strong key="email" />]}
          />
        </p>
        <p className="help">{t('register.checkEmail.resendHint')}</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>{t('register.title')}</h2>
      <div style={{ margin: '16px 0' }}>
        <GoogleSignInButton label={t('register.googleCta')} />
      </div>
      <div
        style={{
          textAlign: 'center',
          color: 'var(--color-text-muted)',
          fontSize: '0.85em',
          margin: '12px 0',
        }}
      >
        {t('register.emailSignInDivider')}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMessage(null);
          setErrorCode(null);
          setSuggestions(null);
          setStatus('submitting');
          void (async () => {
            try {
              await authApi.register({
                email,
                password,
                name,
                ...(company.trim() ? { company_name: company.trim() } : {}),
              });
              // The account exists and the verification mail is out; the
              // customer has not verified yet. `company_stated` is a boolean,
              // never the company itself — no PII crosses the seam (ADR-014).
              trackAuthEvent(ANALYTICS_EVENTS.register_succeeded, email, {
                company_stated: Boolean(company.trim()),
              });
              setStatus('sent');
            } catch (err) {
              const envelope = extractApiError(err);
              setErrorMessage(envelope?.message ?? t('register.errors.registrationFailed'));
              setErrorCode(envelope?.code ?? null);
              const sugg = envelope?.details?.suggestions;
              if (Array.isArray(sugg)) setSuggestions(sugg as string[]);
              setStatus('idle');
            }
          })();
        }}
      >
        <div className="form-row">
          <label htmlFor="name">{t('register.fields.name.label')}</label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-row">
          <label htmlFor="company">{t('register.fields.company.label')}</label>
          <input
            id="company"
            type="text"
            autoComplete="organization"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>
        <div className="form-row">
          <label htmlFor="email">{t('register.fields.email.label')}</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="form-row">
          <label htmlFor="password">{t('register.fields.password.label')}</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="help">{t('register.fields.password.help')}</span>
        </div>
        {errorMessage && (
          <div className="banner banner--error">
            <strong>{errorMessage}</strong>
            {errorCode === 'EMAIL_ALREADY_REGISTERED' && (
              <div style={{ marginTop: 8 }}>
                <Trans
                  i18nKey="register.errors.signInOrReset"
                  t={t}
                  components={[
                    <Link key="signin" to="/login" state={{ email }} />,
                    <Link key="reset" to="/password-reset" state={{ email }} />,
                  ]}
                />
              </div>
            )}
            {suggestions && suggestions.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {suggestions.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <button className="primary" type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? t('register.submitting') : t('common:nav.createAccount')}
        </button>
        <p style={{ marginTop: 16 }}>
          <Trans
            i18nKey="register.signInPrompt"
            t={t}
            components={[<Link key="signin" to="/login" />]}
          />
        </p>
      </form>
    </div>
  );
}
